/**
 * Templates da Meta com variáveis: lógica pura do seletor único e do editor
 * "O que entra aqui?" (2026-09-14).
 *
 * Espelha o backend:
 * - services/wa_business/template_components.py: o que cada template exige;
 * - services/wa_business/template_params_check.py: o que a plataforma monta;
 * - services/message_variables.py: as variáveis que o envio resolve por contato.
 *
 * O valor gravado continua sendo o que o motor já entende (texto fixo,
 * {{primeiro_nome}}, {{campo:Nome}}): nenhum formato novo no banco.
 *
 * Forma dos valores em todas as telas (a mesma do envio):
 *   { body_params: ['{{primeiro_nome}}', 'Pedido 12'],
 *     header_param: null | { type: 'text', text } | { type: 'image', link },
 *     button_params: { '0': '{{campo:Código}}' } }
 */

const POSITIONAL_RE = /\{\{\s*(\d+)\s*\}\}/g;
const NAMED_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
const VAR_RE = /\{\{\s*([^{}\n]+?)\s*\}\}/g;
const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

const SUPPORTED_COMPONENTS = ['HEADER', 'BODY', 'FOOTER', 'BUTTONS'];
const SUPPORTED_HEADER_FORMATS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
export const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const SUPPORTED_BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];

export const MEDIA_NOUN = { IMAGE: 'a imagem', VIDEO: 'o vídeo', DOCUMENT: 'o documento' };
export const MEDIA_LABEL = { IMAGE: 'Imagem', VIDEO: 'Vídeo', DOCUMENT: 'Documento' };

// O que cada tela consegue mandar.
export const CAPS_FULL = { body: true, header: true, media: true, buttons: true };
export const CAPS_BODY = { body: true, header: false, media: false, buttons: false };
export const CAPS_NONE = { body: false, header: false, media: false, buttons: false };

// As opções de "O que entra aqui?". `missing` é como a falta aparece na frase
// "Quem não tiver ... não recebe"; `always` = todo contato tem.
export const VALUE_SOURCES = [
  { key: 'primeiro_nome', label: 'Primeiro nome', token: '{{primeiro_nome}}', missing: 'primeiro nome cadastrado' },
  { key: 'nome', label: 'Nome completo', token: '{{nome}}', missing: 'nome cadastrado' },
  { key: 'saudacao', label: 'Saudação', token: '{{saudacao}}', always: true },
  { key: 'responsavel', label: 'Responsável', token: '{{responsavel}}', missing: 'responsável' },
  { key: 'telefone', label: 'Telefone', token: '{{telefone}}', always: true },
  { key: 'data', label: 'Data', token: '{{data}}', always: true },
  { key: 'hora', label: 'Hora', token: '{{hora}}', always: true },
  { key: 'campo', label: 'Campo personalizado' },
  { key: 'fixo', label: 'Texto fixo' },
];

const TOKEN_SOURCES = VALUE_SOURCES.filter((s) => s.token);

export const normKey = (s) => String(s || '')
  .normalize('NFD').replace(DIACRITICS, '')
  .trim().toLowerCase().replace(/[-\s]+/g, '_');

const positions = (text) => {
  const found = new Set();
  for (const m of String(text || '').matchAll(POSITIONAL_RE)) found.add(parseInt(m[1], 10));
  return Array.from(found).sort((a, b) => a - b);
};

const namedVars = (text) => {
  const out = [];
  for (const m of String(text || '').matchAll(NAMED_RE)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
};

const joinOr = (items) => {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} ou ${items[items.length - 1]}`;
};

/** O que o template exige e se a plataforma consegue montar o envio. */
export function analyzeTemplate(template) {
  const out = {
    name: template?.name || '',
    language: template?.language || '',
    approved: String(template?.status || '').toUpperCase() === 'APPROVED',
    header: null,
    body: { text: '', params: [] },
    footer: '',
    buttons: [],
    dynamicButtons: [],
    unsupported: null,
    requiresParams: false,
  };
  const named = [];
  const unsupported = (reason) => { if (!out.unsupported) out.unsupported = reason; };

  for (const comp of template?.components || []) {
    const type = String(comp.type || '').toUpperCase();
    if (type === 'CAROUSEL') { unsupported('Template em carrossel ainda não é suportado.'); continue; }
    if (type === 'LIMITED_TIME_OFFER') { unsupported('Template de oferta com prazo ainda não é suportado.'); continue; }
    if (!SUPPORTED_COMPONENTS.includes(type)) { unsupported('Este formato de template ainda não é suportado.'); continue; }
    if (type === 'HEADER') {
      const format = String(comp.format || 'TEXT').toUpperCase();
      if (format === 'LOCATION') unsupported('Cabeçalho de localização ainda não é suportado.');
      else if (!SUPPORTED_HEADER_FORMATS.includes(format)) unsupported('Este tipo de cabeçalho ainda não é suportado.');
      out.header = {
        format,
        text: comp.text || '',
        params: format === 'TEXT' ? positions(comp.text) : [],
        needsMedia: MEDIA_HEADER_FORMATS.includes(format),
      };
      if (format === 'TEXT') named.push(...namedVars(comp.text));
    } else if (type === 'BODY') {
      out.body = { text: comp.text || '', params: positions(comp.text) };
      named.push(...namedVars(comp.text));
    } else if (type === 'FOOTER') {
      out.footer = comp.text || '';
    } else if (type === 'BUTTONS') {
      (comp.buttons || []).forEach((btn, index) => {
        const btnType = String(btn.type || '').toUpperCase();
        if (!SUPPORTED_BUTTON_TYPES.includes(btnType)) {
          unsupported('Botão de catálogo, fluxo ou código de autenticação ainda não é suportado.');
        }
        const url = btn.url || '';
        const dynamic = (btnType === 'URL' && positions(url).length > 0) || btnType === 'COPY_CODE';
        const item = { index, type: btnType, text: btn.text || '', url, dynamic };
        out.buttons.push(item);
        if (dynamic) {
          out.dynamicButtons.push({
            ...item,
            subType: btnType === 'COPY_CODE' ? 'copy_code' : 'url',
            baseUrl: url.replace(/\{\{\s*1\s*\}\}.*$/, ''),
          });
        }
      });
    }
  }
  const uniqueNamed = Array.from(new Set(named));
  if (uniqueNamed.length) {
    // Parametro nomeado da Meta ({{nome}}, parameter_format NAMED): suportado
    // desde 2026-10-10. Os valores vao na ordem de aparicao dos nomes
    // (body_params[i] <-> nome i) e o backend monta o payload com
    // parameter_name. Aqui o texto vira posicional para o editor e a previa
    // funcionarem como sempre; os nomes ficam em namedParams para os rotulos.
    const toPositional = (text, names) => names.reduce(
      (acc, name, i) => acc.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), `{{${i + 1}}}`),
      text,
    );
    const bodyNames = namedVars(out.body.text);
    if (bodyNames.length) {
      out.body = {
        text: toPositional(out.body.text, bodyNames),
        params: bodyNames.map((_, i) => i + 1),
        namedParams: bodyNames,
      };
    }
    if (out.header && out.header.format === 'TEXT') {
      const headerNames = namedVars(out.header.text);
      if (headerNames.length) {
        out.header.text = toPositional(out.header.text, headerNames.slice(0, 1));
        out.header.params = [1];
        out.header.namedParams = headerNames.slice(0, 1);
      }
    }
  }
  out.requiresParams = Boolean(
    out.body.params.length
    || (out.header && (out.header.params.length || out.header.needsMedia))
    || out.dynamicButtons.length
    || uniqueNamed.length
  );
  return out;
}

/** Aprovados, sem repetição de nome+idioma, em ordem alfabética. */
export function uniqueApproved(templates) {
  const byKey = new Map();
  (templates || []).forEach((t) => {
    if (!t || !t.name) return;
    if (String(t.status || '').toUpperCase() !== 'APPROVED') return;
    const key = `${t.name}|${t.language || ''}`;
    if (!byKey.has(key)) byKey.set(key, t);
  });
  return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function findTemplate(templates, name, language) {
  if (!name) return null;
  const list = templates || [];
  return list.find((t) => t.name === name && (!language || t.language === language))
    || list.find((t) => t.name === name)
    || null;
}

/** A tela consegue usar este template? Se não, por quê (texto para o cliente). */
export function templateUsability(analysis, caps = CAPS_FULL) {
  if (!analysis) return { usable: false, reason: null };
  if (analysis.unsupported) return { usable: false, reason: analysis.unsupported };
  const c = { ...CAPS_NONE, ...(caps || {}) };
  if (analysis.body.params.length && !c.body) {
    return { usable: false, reason: 'Tem variáveis: esta tela ainda não preenche variáveis.' };
  }
  if (analysis.header?.params.length && !c.header) {
    return { usable: false, reason: 'Tem variável no cabeçalho: esta tela ainda não preenche o cabeçalho.' };
  }
  if (analysis.header?.needsMedia && !c.media) {
    return { usable: false, reason: 'Tem imagem, vídeo ou documento no cabeçalho: esta tela ainda não envia a mídia.' };
  }
  if (analysis.dynamicButtons.length && !c.buttons) {
    return { usable: false, reason: 'Tem botão com link variável ou código: esta tela ainda não preenche botões.' };
  }
  return { usable: true, reason: null };
}

/** Marcadores do seletor. */
export function templateBadges(analysis) {
  if (!analysis) return [];
  const badges = [];
  if (analysis.body.params.length || analysis.header?.params.length) badges.push('Variáveis');
  if (analysis.header?.needsMedia) badges.push(MEDIA_LABEL[analysis.header.format] || 'Mídia');
  if (analysis.buttons.length) badges.push('Botões');
  return badges;
}

/**
 * Capacidades por tela. `flags` vem de /api/system/features.
 * - inbox e tela Templates da Meta: a rota de envio aceita tudo;
 * - campanha, agendamento, follow-up e régua: corpo; cabeçalho e botões com
 *   template_params_automations;
 * - demais (trilha, lembrete, fluxo, saudação, template padrão, Lead Ads,
 *   abertura do agente): nada antes da leva 2.
 */
export function templateCapabilities(screen, flags) {
  const full = Boolean(flags && flags.template_params_automations);
  switch (screen) {
    case 'inbox':
    case 'send_page':
      return CAPS_FULL;
    case 'campaign':
    case 'schedule':
    case 'followup':
    case 'ruler':
      return full ? CAPS_FULL : CAPS_BODY;
    default:
      return full ? CAPS_FULL : CAPS_NONE;
  }
}

export const emptyTemplateValues = () => ({ body_params: [], header_param: null, button_params: {} });

export function normalizeValues(values) {
  const v = values || {};
  return {
    body_params: Array.isArray(v.body_params) ? v.body_params : [],
    header_param: v.header_param && typeof v.header_param === 'object' ? v.header_param : null,
    button_params: v.button_params && typeof v.button_params === 'object' ? v.button_params : {},
  };
}

/** Valores guardados numa mensagem, degrau ou bloco. */
export const valuesFromMessage = (msg) => normalizeValues({
  body_params: msg?.template_params,
  header_param: msg?.header_param,
  button_params: msg?.button_params,
});

/** Valores do template padrão fora da janela de 24 horas. */
export const valuesFromFallback = (holder) => normalizeValues({
  body_params: holder?.fallback_template_params,
  header_param: holder?.fallback_header_param,
  button_params: holder?.fallback_button_params,
});

/** Parse de UM valor salvo: qual opção de "O que entra aqui?" ele é. */
export function parseValue(raw, fields = []) {
  const text = raw == null ? '' : String(raw);
  const trimmed = text.trim();
  if (!trimmed) return { source: '', text: '' };
  const lower = trimmed.toLowerCase();
  if (lower === '[nome]') return { source: 'primeiro_nome' };
  if (lower === '[seu nome]') return { source: 'responsavel' };
  const match = trimmed.match(/^\{\{\s*([^{}\n]+?)\s*\}\}$/);
  if (match) {
    const campo = match[1].match(/^campo\s*:\s*(.+)$/i);
    if (campo) {
      const wanted = normKey(campo[1]);
      const def = (fields || []).find((f) => normKey(f.name) === wanted);
      return {
        source: 'campo',
        field: def ? def.name : campo[1].trim(),
        fieldMissing: !def && (fields || []).length > 0,
      };
    }
    const key = normKey(match[1]);
    if (TOKEN_SOURCES.some((s) => s.key === key)) return { source: key };
  }
  return { source: 'fixo', text };
}

/** O contrário do parse: o texto que o motor entende. */
export function serializeChoice(choice) {
  if (!choice || !choice.source) return '';
  if (choice.source === 'fixo') return choice.text || '';
  if (choice.source === 'campo') return choice.field ? `{{campo:${choice.field}}}` : '';
  return TOKEN_SOURCES.find((s) => s.key === choice.source)?.token || '';
}

/** Rótulo curto de um valor (chip na frase, prévia sem contato). */
export function describeValue(raw, fields = []) {
  const parsed = parseValue(raw, fields);
  if (!parsed.source) return '';
  if (parsed.source === 'campo') return parsed.field;
  if (parsed.source === 'fixo') {
    const t = parsed.text.trim();
    return t.length > 28 ? `${t.slice(0, 28)}…` : t;
  }
  return VALUE_SOURCES.find((s) => s.key === parsed.source)?.label || '';
}

const withLegacyAliases = (text) => String(text || '')
  .replace(/\[seu nome\]/gi, '{{responsavel}}')
  .replace(/\[nome\]/gi, '{{primeiro_nome}}');

/** Troca as NOSSAS variáveis por [Rótulo]; {{n}} da Meta fica intacto. */
export function tokensToLabels(text) {
  return withLegacyAliases(text).replace(VAR_RE, (whole, inner) => {
    const campo = inner.match(/^campo\s*:\s*(.+)$/i);
    if (campo) return `[${campo[1].trim()}]`;
    const src = TOKEN_SOURCES.find((s) => s.key === normKey(inner));
    return src ? `[${src.label}]` : whole;
  });
}

const fillPositional = (text, getValue) => String(text || '').replace(POSITIONAL_RE, (whole, n) => {
  const value = getValue(parseInt(n, 10));
  return value && String(value).trim() ? tokensToLabels(value) : whole;
});

/** Prévia sem contato: cada escolha aparece como [Rótulo]. */
export function labelPreview(analysis, values) {
  const v = normalizeValues(values);
  const header = analysis?.header;
  return {
    header: header
      ? {
        format: header.format,
        text: header.needsMedia ? '' : fillPositional(header.text, () => v.header_param?.text),
        media_link: header.needsMedia ? (v.header_param?.link || '') : '',
      }
      : null,
    body_text: fillPositional(analysis?.body.text, (n) => v.body_params[n - 1]),
    footer_text: analysis?.footer || '',
    buttons: (analysis?.buttons || []).map((b) => ({
      ...b,
      url: b.dynamic && b.type === 'URL'
        ? fillPositional(b.url, () => v.button_params[String(b.index)])
        : b.url,
    })),
  };
}

/** O que falta preencher (texto para o cliente). Vazio = pode salvar/enviar. */
/** Rotulo do placeholder n do corpo: `{{nome}}` em template nomeado, senao `{{n}}`. */
export function bodyTokenLabel(analysis, n) {
  const name = analysis?.body?.namedParams?.[n - 1];
  return name ? `{{${name}}}` : `{{${n}}}`;
}

/** Rotulo do placeholder do cabecalho de texto. */
export function headerTokenLabel(analysis) {
  const name = analysis?.header?.namedParams?.[0];
  return name ? `{{${name}}}` : '{{1}}';
}

export function missingValues(analysis, values, caps = CAPS_FULL) {
  if (!analysis) return [];
  const usability = templateUsability(analysis, caps);
  if (!usability.usable) return [usability.reason];
  const v = normalizeValues(values);
  const out = [];
  const header = analysis.header;
  if (header?.needsMedia) {
    if (!String(v.header_param?.link || '').trim()) out.push(`Falta ${MEDIA_NOUN[header.format] || 'a mídia'} do cabeçalho.`);
  } else if (header?.params.length) {
    if (!String(v.header_param?.text || '').trim()) out.push(`Falta escolher o que entra em ${headerTokenLabel(analysis)} no cabeçalho.`);
  }
  analysis.body.params.forEach((n) => {
    if (!String(v.body_params[n - 1] || '').trim()) out.push(`Falta escolher o que entra em ${bodyTokenLabel(analysis, n)} no corpo.`);
  });
  analysis.dynamicButtons.forEach((b) => {
    if (!String(v.button_params[String(b.index)] || '').trim()) {
      out.push(b.text ? `Falta o valor do botão "${b.text}".` : `Falta o valor do botão ${b.index + 1}.`);
    }
  });
  return out;
}

/** Payload da rota de envio (inbox e tela Templates da Meta). */
export function buildSendExtra(analysis, values) {
  const v = normalizeValues(values);
  const extra = {};
  if (!analysis) return extra;
  const lastBody = analysis.body.params.length ? Math.max(...analysis.body.params) : 0;
  if (lastBody) {
    extra.body_params = Array.from({ length: lastBody }, (_, i) => String(v.body_params[i] || '').trim());
  }
  const header = analysis.header;
  if (header?.needsMedia) {
    extra.header_param = {
      type: header.format.toLowerCase(),
      link: String(v.header_param?.link || '').trim(),
      ...(header.format === 'DOCUMENT' && v.header_param?.filename ? { filename: v.header_param.filename } : {}),
    };
  } else if (header?.params.length) {
    extra.header_param = { type: 'text', text: String(v.header_param?.text || '').trim() };
  }
  if (analysis.dynamicButtons.length) {
    extra.button_params = {};
    analysis.dynamicButtons.forEach((b) => {
      extra.button_params[String(b.index)] = String(v.button_params[String(b.index)] || '').trim();
    });
  }
  return extra;
}

/**
 * Campos de template de uma automação (mensagem de follow-up, degrau, bloco).
 * Cabeçalho e botões só saem quando a tela consegue mandá-los: sem isso o
 * payload fica igual ao de antes.
 */
export function automationTemplateFields(analysis, values, caps) {
  const extra = buildSendExtra(analysis, values);
  const c = { ...CAPS_NONE, ...(caps || {}) };
  const out = {
    template_params: extra.body_params && extra.body_params.length ? extra.body_params : null,
  };
  if ((c.header || c.media) && extra.header_param) out.header_param = extra.header_param;
  if (c.buttons && extra.button_params) out.button_params = extra.button_params;
  return out;
}

const tokenMissingLabel = (token) => {
  const campo = String(token || '').match(/^campo\s*:\s*(.+)$/i);
  if (campo) return `o campo ${campo[1].trim()} preenchido`;
  const src = TOKEN_SOURCES.find((s) => s.key === normKey(token));
  return src && !src.always ? src.missing : null;
};

/** Dados do contato que os valores usam (para a frase de quem não recebe). */
export function contactDataUsed(analysis, values) {
  const v = normalizeValues(values);
  const raws = [
    ...v.body_params,
    v.header_param?.text,
    ...Object.values(v.button_params),
  ];
  const labels = [];
  raws.forEach((raw) => {
    for (const m of withLegacyAliases(raw).matchAll(VAR_RE)) {
      const label = tokenMissingLabel(m[1]);
      if (label && !labels.includes(label)) labels.push(label);
    }
  });
  return labels;
}

/** Linguagem simples: o que acontece com quem não tem o dado (envio em massa). */
export function missingDataSentence(analysis, values) {
  const labels = contactDataUsed(analysis, values);
  if (!labels.length) return null;
  return `Quem não tiver ${joinOr(labels)} não recebe esta mensagem: `
    + 'o envio para essa pessoa é bloqueado e o motivo fica registrado.';
}

/** Faltas da prévia de UM contato (resposta de /preview), em frases. */
export function previewMissingSentences(missing, analysis) {
  return (missing || []).map((m) => {
    if (m.where === 'header' && analysis?.header?.needsMedia) {
      return `Falta ${MEDIA_NOUN[analysis.header.format] || 'a mídia'} do cabeçalho.`;
    }
    const lugar = m.where === 'header'
      ? 'no cabeçalho'
      : m.where === 'button' ? `no botão ${m.position}` : `em {{${m.position}}} no corpo`;
    if (m.empty) return `Falta escolher o que entra ${lugar}.`;
    const labels = (m.tokens || []).map(tokenMissingLabel).filter(Boolean);
    return `Este contato não tem ${joinOr(labels) || 'o dado escolhido'}: escolha outra opção ${lugar} ou use texto fixo.`;
  });
}
